import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { signupThunk } from '../redux/userSlice'
import { AppDispatch } from '../store'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Label } from '../components/ui/label'
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Checkbox } from '../components/ui/checkbox'
import {
  TermsOfUseContent,
  PrivacyPolicyContent,
  CURRENT_LEGAL_VERSION,
} from '@adthlete/shared-frontend'

const Signup: React.FC = () => {
  const [searchParams] = useSearchParams()
  const pendingInviteToken = localStorage.getItem('pendingOrgInviteToken')
  const inviteOrgName = pendingInviteToken ? searchParams.get('orgName') : null

  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [name, setName] = useState('')
  const [orgAction, setOrgAction] = useState<'create' | 'join'>('create')
  const [orgName, setOrgName] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [agreedToTerms, setAgreedToTerms] = useState(false)

  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (!pendingInviteToken && orgAction === 'create' && !orgName.trim()) {
      setError('Organization name is required to create a new organization')
      return
    }

    if (!agreedToTerms) {
      setError('You must agree to the Terms of Use and Privacy Policy to continue.')
      return
    }

    setIsLoading(true)

    try {
      await dispatch(
        signupThunk({
          email,
          password,
          name,
          user_type: 'marketer',
          org_name: !pendingInviteToken && orgAction === 'create' ? orgName : undefined,
          invite_token: pendingInviteToken || undefined,
          agreed_to_terms: agreedToTerms,
          legal_version: CURRENT_LEGAL_VERSION,
        }),
      ).unwrap()

      if (pendingInviteToken) {
        localStorage.removeItem('pendingOrgInviteToken')
      }

      const returnTo = searchParams.get('returnTo')
      const confirmationUrl = `/confirmation-sent?email=${encodeURIComponent(email)}${returnTo ? `&returnTo=${encodeURIComponent(returnTo)}` : ''}`
      navigate(confirmationUrl)
    } catch (err: any) {
      setError(err.message || 'Registration failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center py-8 relative">
      <div className="fixed inset-0 -z-10 bg-[#F4F7EF]"></div>

      <div className="w-full max-w-md px-4">
        <Card className="rounded-[28px] border-0 bg-[#FFFFFF] py-4 shadow-[0_20px_60px_rgba(16,24,40,0.08)] sm:px-4">
          <CardHeader className="text-center pb-0">
            <div className="flex justify-center mb-4">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardTitle className="text-3xl font-black text-[#0F172A] tracking-tight">
              Create an account
            </CardTitle>
            <CardDescription className="mt-2 text-[#4B5563]">
              Enter your information to get started
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {error && <p className="text-red-500 mb-4 text-sm">{error}</p>}
            <form onSubmit={handleSubmit} data-track-form="Brand sign-up" className="space-y-4">
              <div>
                <Label className="text-[#0F172A]">Full Name</Label>
                <Input
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="h-12 rounded-xl border-[#E5E7EB] bg-[#FFFFFF] text-base text-[#111827] focus-visible:border-[#84CC16] focus-visible:ring-[#C0FD72]/40 mt-1"
                  required
                />
              </div>

              <div>
                <Label className="text-[#0F172A]">Email</Label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl border-[#E5E7EB] bg-[#FFFFFF] text-base text-[#111827] focus-visible:border-[#84CC16] focus-visible:ring-[#C0FD72]/40 mt-1"
                  required
                />
              </div>

              <div>
                <Label className="text-[#0F172A]">Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-12 rounded-xl border-[#E5E7EB] bg-[#FFFFFF] text-base text-[#111827] focus-visible:border-[#84CC16] focus-visible:ring-[#C0FD72]/40 mt-1"
                  required
                />
              </div>

              <div>
                <Label className="text-[#0F172A]">Confirm Password</Label>
                <Input
                  type="password"
                  placeholder="••••••••"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="h-12 rounded-xl border-[#E5E7EB] bg-[#FFFFFF] text-base text-[#111827] focus-visible:border-[#84CC16] focus-visible:ring-[#C0FD72]/40 mt-1"
                  required
                />
              </div>

              {/* Organization Section */}
              {pendingInviteToken ? (
                <div className="pt-2 border-t border-[#E5E7EB]">
                  <div className="bg-[#C0FD72]/10 border border-[#C0FD72]/20 rounded p-3 text-[#3F7A0A] text-sm">
                    You'll be joining <strong>{inviteOrgName || 'an organization'}</strong> via
                    your invitation after signing up.
                  </div>
                </div>
              ) : (
                <div className="pt-2 border-t border-[#E5E7EB]">
                  <Label className="text-[#0F172A] text-base mb-3 block">Organization</Label>
                  <RadioGroup
                    defaultValue="create"
                    value={orgAction}
                    onValueChange={(val) => setOrgAction(val as 'create' | 'join')}
                    className="flex flex-col gap-3 mb-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="create"
                        id="r1"
                        className="border-foreground text-[#3F7A0A]"
                      />
                      <Label htmlFor="r1" className="text-[#374151] font-normal">
                        Create a new Organization
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem
                        value="join"
                        id="r2"
                        className="border-foreground text-[#3F7A0A]"
                      />
                      <Label htmlFor="r2" className="text-[#374151] font-normal">
                        Join an existing Organization
                      </Label>
                    </div>
                  </RadioGroup>

                  {orgAction === 'create' ? (
                    <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                      <Label className="text-[#0F172A]">Organization Name</Label>
                      <Input
                        placeholder="Acme Corp"
                        value={orgName}
                        onChange={(e) => setOrgName(e.target.value)}
                        className="h-12 rounded-xl border-[#E5E7EB] bg-[#FFFFFF] text-base text-[#111827] focus-visible:border-[#84CC16] focus-visible:ring-[#C0FD72]/40 mt-1"
                        required
                      />
                    </div>
                  ) : (
                    <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                      <div className="bg-yellow-500/10 border border-yellow-500/20 rounded p-3 text-yellow-800 text-sm">
                        To join an existing organization, please ask an administrator to send you
                        an invitation email.
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-start space-x-3 pt-2 border-t border-[#E5E7EB]">
                <Checkbox
                  id="agree-terms"
                  checked={agreedToTerms}
                  onCheckedChange={(checked) => setAgreedToTerms(checked as boolean)}
                  className="border-[#E5E7EB] data-[state=checked]:bg-[#C0FD72] data-[state=checked]:border-[#C0FD72] mt-1"
                />
                <label
                  htmlFor="agree-terms"
                  className="text-sm text-[#374151] leading-relaxed cursor-pointer"
                >
                  I agree to the{' '}
                  <Link to="/terms" className="text-[#3F7A0A] hover:text-[#3F7A0A] underline">
                    Terms of Use and Privacy Policy
                  </Link>{' '}
                  outlined below.
                </label>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#C0FD72] hover:bg-[#a8e45a] text-black font-medium mt-4"
                disabled={
                  isLoading || (!pendingInviteToken && orgAction === 'join') || !agreedToTerms
                }
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-[#4B5563] text-sm">
                Already have an account?{' '}
                <Link to="/signin" className="text-[#3F7A0A] hover:text-[#3F7A0A] font-medium">
                  Log in
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-6 mt-6">
          <TermsOfUseContent maxHeight="h-64" />
          <PrivacyPolicyContent maxHeight="h-64" />
        </div>
      </div>
    </div>
  )
}

export default Signup
